import { memo, useEffect, useMemo, useRef } from 'react';
import type { MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Play, Trash2, UserRound } from 'lucide-react';
import type { Schedule, ScheduleEditorState } from '../../types/scheduledCommands';
import type { ScheduleState } from '../../utils/scheduledCommands';
import { commandPreview, scheduleDisplayName, scheduleState } from '../../utils/scheduledCommands';
import { compileTellraw, parseTellraw } from '../../utils/tellraw';
import { formatInterval } from '../../utils/interval';
import { TellrawPreview } from './tellraw/TellrawPreview';
import { LastRun, RelativeTime, ScheduleStateBadge, TargetChips } from './ScheduleBits';
import { formatReleaseAt } from '../../utils/comingSoon';
import { cn } from '../../utils/cn';

export interface ScheduleRowActions {
  onToggle: (schedule: Schedule, enabled: boolean) => void;
  onRun: (schedule: Schedule) => void;
  onSendToPlayer: (schedule: Schedule) => void;
  onDuplicate: (schedule: Schedule) => void;
  onDelete: (schedule: Schedule) => void;
}

interface ScheduleTableProps extends ScheduleRowActions {
  schedules: Schedule[];
  nowSec: number;
  canWrite: boolean;
  selected: Set<number>;
  onSelectedChange: (next: Set<number>) => void;
  /** Ids whose switch is mid-flight, so it cannot be flipped twice. */
  toggling: Set<number>;
  /** The row to scroll to and mark, e.g. after clicking a run on the timeline. */
  focusId: number | null;
  onHover: (id: number | null) => void;
}

/**
 * What a schedule shows players, for the one-line preview: the composer's own
 * message when it has one, otherwise the command parsed as a tellraw, otherwise
 * nothing (a plain console command is shown as text instead).
 */
function previewComponent(schedule: Schedule): unknown | null {
  const state: ScheduleEditorState | null = schedule.editor_state;
  if (state?.kind === 'tellraw') return compileTellraw(state.message, state.compat, state.selector).component;
  const parsed = parseTellraw(schedule.command);
  return parsed.ok ? parsed.component : null;
}

const NextRun = ({ schedule, state }: { schedule: Schedule; state: ScheduleState }) => {
  if (state === 'paused') return <span className="text-xs text-glass-500">Paused</span>;
  if (state === 'ended') {
    return <span className="text-xs text-glass-500">{schedule.ends_at ? `Ended ${formatReleaseAt(schedule.ends_at)}` : 'Ended'}</span>;
  }
  if (schedule.next_run_at === null) return <span className="text-xs text-glass-500">—</span>;
  return (
    <span className="text-xs">
      {state === 'not_started' && <span className="text-glass-400">Starts </span>}
      <RelativeTime at={schedule.next_run_at} className="text-glass-300" />
    </span>
  );
};

interface RowProps extends ScheduleRowActions {
  schedule: Schedule;
  state: ScheduleState;
  preview: unknown | null;
  canWrite: boolean;
  checked: boolean;
  toggling: boolean;
  focused: boolean;
  onCheck: (id: number, checked: boolean) => void;
  onHover: (id: number | null) => void;
  onOpen: (id: number) => void;
}

const stop = (e: MouseEvent) => e.stopPropagation();

const RowActions = ({ schedule, canWrite, onRun, onSendToPlayer, onDuplicate, onDelete }: RowProps) => {
  if (!canWrite) return null;
  const canNarrow = /@a/.test(schedule.command);
  return (
    <div className="flex items-center justify-end gap-0.5" onClick={stop}>
      <button type="button" onClick={() => onRun(schedule)} className="btn-icon btn-icon-sm touch-manipulation" title="Run now" aria-label="Run now">
        <Play className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => onSendToPlayer(schedule)}
        className="btn-icon btn-icon-sm touch-manipulation"
        title={canNarrow ? 'Send to one player' : 'Only commands that target @a can be sent to one player'}
        aria-label="Send to one player"
        disabled={!canNarrow}
      >
        <UserRound className="w-4 h-4" />
      </button>
      <button type="button" onClick={() => onDuplicate(schedule)} className="btn-icon btn-icon-sm touch-manipulation" title="Duplicate" aria-label="Duplicate">
        <Copy className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => onDelete(schedule)}
        className="btn-icon btn-icon-sm touch-manipulation text-red-400 hover:text-red-300"
        title="Delete"
        aria-label="Delete"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

const Toggle = ({ schedule, state, canWrite, toggling, onToggle }: RowProps) => {
  if (!canWrite) return <ScheduleStateBadge state={state} />;
  const ended = state === 'ended';
  return (
    <label
      className={cn('switch', (toggling || ended) && 'opacity-50')}
      onClick={stop}
      title={ended ? 'The end date has passed. Open the schedule to move it.' : schedule.enabled ? 'Pause' : 'Resume'}
    >
      <input
        type="checkbox"
        checked={schedule.enabled && !ended}
        disabled={toggling || ended}
        onChange={(e) => onToggle(schedule, e.target.checked)}
        aria-label={`${schedule.enabled ? 'Pause' : 'Resume'} ${scheduleDisplayName(schedule)}`}
      />
      <span />
    </label>
  );
};

const Summary = ({ schedule, preview }: { schedule: Schedule; preview: unknown | null }) => {
  const named = !!schedule.name?.trim();
  return (
    <div className="min-w-0 space-y-1">
      {named ? (
        <p className="text-sm font-medium text-white truncate">{scheduleDisplayName(schedule)}</p>
      ) : (
        // Unnamed: the command is the name, so it is shown once, as code.
        !preview && <p className="font-mono text-xs text-white truncate max-w-[28rem]">{commandPreview(schedule.command)}</p>
      )}
      {preview ? (
        <TellrawPreview component={preview} compact className="max-w-full" />
      ) : (
        named && <p className="font-mono text-xs text-glass-400 truncate max-w-[28rem]">{commandPreview(schedule.command)}</p>
      )}
    </div>
  );
};

const TableRow = memo((props: RowProps) => {
  const { schedule, state, preview, canWrite, checked, focused, onCheck, onHover, onOpen } = props;
  const ref = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focused]);
  return (
    <tr
      ref={ref}
      className={cn('cursor-pointer', (checked || focused) && 'row-active')}
      onClick={() => onOpen(schedule.id)}
      onMouseEnter={() => onHover(schedule.id)}
      onMouseLeave={() => onHover(null)}
    >
      {canWrite && (
        <td className="td w-10" onClick={stop}>
          <input
            type="checkbox"
            className="form-checkbox"
            checked={checked}
            onChange={(e) => onCheck(schedule.id, e.target.checked)}
            aria-label={`Select ${scheduleDisplayName(schedule)}`}
          />
        </td>
      )}
      <td className="td max-w-0 w-[40%] pr-6">
        <Summary schedule={schedule} preview={preview} />
      </td>
      <td className="td"><TargetChips targets={schedule.targets} /></td>
      <td className="td font-mono text-xs text-glass-300 tabular-nums whitespace-nowrap">{formatInterval(schedule.interval_seconds)}</td>
      <td className="td whitespace-nowrap"><NextRun schedule={schedule} state={state} /></td>
      <td className="td whitespace-nowrap"><LastRun run={schedule.last_run} /></td>
      <td className="td"><Toggle {...props} /></td>
      {canWrite && <td className="td"><RowActions {...props} /></td>}
    </tr>
  );
});
TableRow.displayName = 'ScheduleTableRow';

const MobileCard = memo((props: RowProps) => {
  const { schedule, state, preview, canWrite, checked, onCheck, onOpen } = props;
  return (
    <div
      className={cn('glass-card p-4 space-y-3', checked && 'border-accent-400/40')}
      onClick={() => onOpen(schedule.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen(schedule.id);
      }}
    >
      <div className="flex items-start gap-3">
        {canWrite && (
          <input
            type="checkbox"
            className="form-checkbox mt-0.5"
            checked={checked}
            onClick={stop}
            onChange={(e) => onCheck(schedule.id, e.target.checked)}
            aria-label={`Select ${scheduleDisplayName(schedule)}`}
          />
        )}
        <div className="flex-1 min-w-0">
          <Summary schedule={schedule} preview={preview} />
        </div>
        <Toggle {...props} />
      </div>
      <TargetChips targets={schedule.targets} max={3} />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-glass-400">
        <span>Every <span className="font-mono text-glass-300">{formatInterval(schedule.interval_seconds)}</span></span>
        <span>Next <NextRun schedule={schedule} state={state} /></span>
        <LastRun run={schedule.last_run} />
      </div>
      {canWrite && <RowActions {...props} />}
    </div>
  );
});
MobileCard.displayName = 'ScheduleMobileCard';

export const ScheduleTable = ({
  schedules,
  nowSec,
  canWrite,
  selected,
  onSelectedChange,
  toggling,
  focusId,
  onHover,
  ...actions
}: ScheduleTableProps) => {
  const navigate = useNavigate();
  const headerRef = useRef<HTMLInputElement>(null);
  const previews = useMemo(() => new Map(schedules.map((s) => [s.id, previewComponent(s)])), [schedules]);

  const allChecked = schedules.length > 0 && schedules.every((s) => selected.has(s.id));
  const someChecked = schedules.some((s) => selected.has(s.id));
  useEffect(() => {
    if (headerRef.current) headerRef.current.indeterminate = someChecked && !allChecked;
  }, [someChecked, allChecked]);

  const onCheck = (id: number, checked: boolean) => {
    const next = new Set(selected);
    if (checked) next.add(id);
    else next.delete(id);
    onSelectedChange(next);
  };

  const toggleAll = () => {
    const next = new Set(selected);
    if (allChecked) schedules.forEach((s) => next.delete(s.id));
    else schedules.forEach((s) => next.add(s.id));
    onSelectedChange(next);
  };

  const onOpen = (id: number) => navigate(`/scheduled-commands/${id}`);

  const rowProps = (schedule: Schedule): RowProps => ({
    ...actions,
    schedule,
    state: scheduleState(schedule, nowSec),
    preview: previews.get(schedule.id) ?? null,
    canWrite,
    checked: selected.has(schedule.id),
    toggling: toggling.has(schedule.id),
    focused: focusId === schedule.id,
    onCheck,
    onHover,
    onOpen,
  });

  return (
    <>
      <div className="hidden md:block table-wrap max-h-[70vh]" data-tour="schedules-table">
        <table className="table">
          <thead>
            <tr>
              {canWrite && (
                <th className="th w-10">
                  <input
                    ref={headerRef}
                    type="checkbox"
                    className="form-checkbox"
                    checked={allChecked}
                    onChange={toggleAll}
                    aria-label={allChecked ? 'Clear the selection' : 'Select every schedule shown'}
                  />
                </th>
              )}
              <th className="th">Schedule</th>
              <th className="th">Targets</th>
              <th className="th">Every</th>
              <th className="th">Next run</th>
              <th className="th">Last run</th>
              <th className="th">{canWrite ? 'On' : 'State'}</th>
              {canWrite && <th className="th text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {schedules.map((schedule) => (
              <TableRow key={schedule.id} {...rowProps(schedule)} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3">
        {canWrite && schedules.length > 0 && (
          <label className="flex items-center gap-2 px-1 text-xs text-glass-400">
            <input type="checkbox" className="form-checkbox" checked={allChecked} onChange={toggleAll} />
            Select all {schedules.length} shown
          </label>
        )}
        {schedules.map((schedule) => (
          <MobileCard key={schedule.id} {...rowProps(schedule)} />
        ))}
      </div>
    </>
  );
};
